import {
	Badge,
	BottomSheet,
	Button,
	Card,
	CardContent,
	CardHeader,
	CardTitle,
	Label,
	ResponsiveTable,
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
	Skeleton,
	Switch,
} from "@ancatag/at-design";
import { ChevronLeft, ChevronRight, RefreshCw } from "lucide-react";
import { useState } from "react";
import { CreateProjectDialog } from "../components/projects/CreateProjectDialog";
import { ProjectEmptyState } from "../components/projects/ProjectEmptyState";
import { useAuth } from "../context/AuthContext";
import { useProjectContext } from "../context/ProjectContext";
import { useProjectAuditLogs } from "../hooks/useAuditLogs";
import type { AuditLog } from "../services/audit";

export function AuditLogs() {
	const {
		selectedProject,
		isLoading: projectLoading,
		refreshProjects,
	} = useProjectContext();
	const [createProjectOpen, setCreateProjectOpen] = useState(false);
	const [page, setPage] = useState(1);
	const [limit] = useState(50);
	const [action, setAction] = useState<string | undefined>(undefined);
	const [resourceType, setResourceType] = useState<string | undefined>(
		undefined,
	);
	const [status, setStatus] = useState<string | undefined>(undefined);
	const [autoRefresh, setAutoRefresh] = useState(false);
	const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);

	// Fetch audit logs
	const { data: logsData, isLoading } = useProjectAuditLogs(
		{
			page,
			limit,
			action,
			resourceType,
			status,
		},
		{ autoRefresh, refreshInterval: 30000 },
	);

	const logs = logsData?.logs || [];
	const totalPages = logsData?.totalPages || 0;

	const getStatusBadge = (status: string) => {
		const variants: Record<string, string> = {
			success: "bg-green-500/20 text-green-600 border-green-500/30",
			failed: "bg-red-500/20 text-red-600 border-red-500/30",
		};
		return (
			variants[status] || "bg-gray-500/20 text-gray-600 border-gray-500/30"
		);
	};

	const getActionLabel = (action: string) => {
		// Format action strings like "user.signup" to "User Signup"
		return action
			.split(".")
			.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
			.join(" ");
	};

	if (projectLoading) {
		return <AuditLogsSkeleton />;
	}

	const { user } = useAuth();
	const isSuperAdmin = user?.role === "superAdmin";

	if (!selectedProject && !isSuperAdmin) {
		return (
			<div className="space-y-6">
				<h1 className="text-3xl font-bold text-foreground">Audit Logs</h1>
				<p className="text-muted-foreground">
					View system activity and administrative actions for your project.
				</p>
				<ProjectEmptyState
					onCreateProject={() => setCreateProjectOpen(true)}
					onRefresh={refreshProjects}
				/>
				<CreateProjectDialog
					open={createProjectOpen}
					onOpenChange={setCreateProjectOpen}
				/>
			</div>
		);
	}

	return (
		<div className="space-y-6">
			<div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
				<div>
					<h1 className="text-2xl md:text-3xl font-bold text-foreground">
						Audit Logs
					</h1>
					<p className="text-muted-foreground text-sm md:text-base">
						System activity for {selectedProject?.name || "your project"}
					</p>
				</div>
				<div className="flex items-center gap-3">
					<div className="flex items-center gap-2">
						<Switch
							id="auto-refresh"
							checked={autoRefresh}
							onCheckedChange={setAutoRefresh}
						/>
						<Label htmlFor="auto-refresh" className="text-sm cursor-pointer">
							Auto-refresh
						</Label>
					</div>
					{autoRefresh && (
						<Badge variant="secondary" className="flex items-center gap-1">
							<RefreshCw className="h-3 w-3 animate-spin" />
							Updating...
						</Badge>
					)}
				</div>
			</div>

			{/* Filters */}
			<Card>
				<CardHeader>
					<CardTitle className="text-lg">Filters</CardTitle>
				</CardHeader>
				<CardContent>
					<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
						<div className="space-y-2">
							<Label className="text-xs text-muted-foreground">Action</Label>
							<Select
								value={action || "all"}
								onValueChange={(value) => {
									setAction(value === "all" ? undefined : value);
									setPage(1);
								}}
							>
								<SelectTrigger>
									<SelectValue placeholder="All Actions" />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="all">All Actions</SelectItem>
									<SelectItem value="user.signup">User Signup</SelectItem>
									<SelectItem value="user.login">User Login</SelectItem>
									<SelectItem value="project.create">Project Create</SelectItem>
									<SelectItem value="project.update">Project Update</SelectItem>
									<SelectItem value="api_key.create">API Key Create</SelectItem>
									<SelectItem value="api_key.revoke">API Key Revoke</SelectItem>
									<SelectItem value="model_config.create">
										Model Config Create
									</SelectItem>
									<SelectItem value="model_config.update">
										Model Config Update
									</SelectItem>
								</SelectContent>
							</Select>
						</div>
						<div className="space-y-2">
							<Label className="text-xs text-muted-foreground">
								Resource Type
							</Label>
							<Select
								value={resourceType || "all"}
								onValueChange={(value) => {
									setResourceType(value === "all" ? undefined : value);
									setPage(1);
								}}
							>
								<SelectTrigger>
									<SelectValue placeholder="All Resources" />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="all">All Resources</SelectItem>
									<SelectItem value="user">User</SelectItem>
									<SelectItem value="project">Project</SelectItem>
									<SelectItem value="api_key">API Key</SelectItem>
									<SelectItem value="model_config">Model Config</SelectItem>
								</SelectContent>
							</Select>
						</div>
						<div className="space-y-2">
							<Label className="text-xs text-muted-foreground">Status</Label>
							<Select
								value={status || "all"}
								onValueChange={(value) => {
									setStatus(value === "all" ? undefined : value);
									setPage(1);
								}}
							>
								<SelectTrigger>
									<SelectValue placeholder="All Statuses" />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="all">All Statuses</SelectItem>
									<SelectItem value="success">Success</SelectItem>
									<SelectItem value="failed">Failed</SelectItem>
								</SelectContent>
							</Select>
						</div>
					</div>
				</CardContent>
			</Card>

			{/* Audit Logs Table */}
			<Card>
				<CardHeader>
					<CardTitle className="text-lg">
						Audit Logs{" "}
						{logsData && `(${logsData.total.toLocaleString()} total)`}
					</CardTitle>
				</CardHeader>
				<CardContent>
					{isLoading ? (
						<AuditLogsTableSkeleton />
					) : logs.length === 0 ? (
						<div className="text-center py-12 text-muted-foreground">
							No audit logs found matching your filters.
						</div>
					) : (
						<>
							<ResponsiveTable
								data={logs}
								keyExtractor={(log) => log.id}
								columns={[
									{
										key: "time",
										header: "Time",
										mobileLabel: "Time",
										render: (log) => (
											<span className="font-mono text-xs">
												{new Date(log.createdAt).toLocaleString()}
											</span>
										),
									},
									{
										key: "user",
										header: "User",
										mobileLabel: "User",
										render: (log) => (
											<span className="text-sm">
												{log.userEmail || log.userId || "System"}
											</span>
										),
									},
									{
										key: "action",
										header: "Action",
										mobileLabel: "Action",
										render: (log) => (
											<span className="text-sm">
												{getActionLabel(log.action)}
											</span>
										),
									},
									{
										key: "resource",
										header: "Resource",
										mobileLabel: "Resource",
										render: (log) => (
											<span className="text-sm">
												{log.resourceType && (
													<>
														{log.resourceType}
														{log.resourceId && (
															<span className="text-muted-foreground ml-1">
																({log.resourceId.slice(0, 8)}...)
															</span>
														)}
													</>
												)}
											</span>
										),
									},
									{
										key: "status",
										header: "Status",
										mobileLabel: "Status",
										render: (log) => (
											<Badge
												variant="outline"
												className={getStatusBadge(log.status)}
											>
												{log.status}
											</Badge>
										),
									},
									{
										key: "ip",
										header: "IP Address",
										mobileLabel: "IP Address",
										render: (log) => (
											<span className="font-mono text-xs">
												{log.ipAddress || "—"}
											</span>
										),
									},
								]}
								onRowClick={(log) => setSelectedLog(log)}
								emptyMessage="No audit logs found matching your filters."
							/>

							{/* Pagination */}
							{totalPages > 1 && (
								<div className="flex items-center justify-between mt-4">
									<div className="text-sm text-muted-foreground">
										Page {page} of {totalPages}
									</div>
									<div className="flex items-center gap-2">
										<Button
											variant="outline"
											size="sm"
											onClick={() => setPage((p) => Math.max(1, p - 1))}
											disabled={page === 1}
										>
											<ChevronLeft className="h-4 w-4" />
											Previous
										</Button>
										<Button
											variant="outline"
											size="sm"
											onClick={() =>
												setPage((p) => Math.min(totalPages, p + 1))
											}
											disabled={page === totalPages}
										>
											Next
											<ChevronRight className="h-4 w-4" />
										</Button>
									</div>
								</div>
							)}
						</>
					)}
				</CardContent>
			</Card>

			{/* Log Detail Bottom Sheet/Dialog */}
			<BottomSheet
				open={!!selectedLog}
				onOpenChange={(open) => !open && setSelectedLog(null)}
				title="Audit Log Details"
				maxHeight="max-h-[85vh]"
			>
				{selectedLog && (
					<div className="space-y-4">
						<div className="text-xs text-muted-foreground mb-4">
							Action: {getActionLabel(selectedLog.action)}
						</div>
						<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
							<div>
								<Label className="text-xs text-muted-foreground">Status</Label>
								<div className="mt-1">
									<Badge
										variant="outline"
										className={getStatusBadge(selectedLog.status)}
									>
										{selectedLog.status}
									</Badge>
								</div>
							</div>
							<div>
								<Label className="text-xs text-muted-foreground">User</Label>
								<div className="mt-1 text-sm">
									{selectedLog.userEmail || selectedLog.userId || "System"}
								</div>
							</div>
							<div>
								<Label className="text-xs text-muted-foreground">Action</Label>
								<div className="mt-1 text-sm">
									{getActionLabel(selectedLog.action)}
								</div>
							</div>
							<div>
								<Label className="text-xs text-muted-foreground">
									Resource Type
								</Label>
								<div className="mt-1 text-sm">
									{selectedLog.resourceType || "—"}
								</div>
							</div>
							<div>
								<Label className="text-xs text-muted-foreground">
									Resource ID
								</Label>
								<div className="mt-1 text-sm font-mono">
									{selectedLog.resourceId || "—"}
								</div>
							</div>
							<div>
								<Label className="text-xs text-muted-foreground">
									IP Address
								</Label>
								<div className="mt-1 text-sm font-mono">
									{selectedLog.ipAddress || "—"}
								</div>
							</div>
							{selectedLog.errorMessage && (
								<div className="col-span-2">
									<Label className="text-xs text-muted-foreground">Error</Label>
									<div className="mt-1 text-sm text-red-600">
										{selectedLog.errorMessage}
									</div>
								</div>
							)}
							<div>
								<Label className="text-xs text-muted-foreground">
									Created At
								</Label>
								<div className="mt-1 text-sm font-mono">
									{new Date(selectedLog.createdAt).toLocaleString()}
								</div>
							</div>
						</div>
						{selectedLog.metadata && (
							<div>
								<Label className="text-xs text-muted-foreground">
									Metadata
								</Label>
								<pre className="mt-1 p-3 bg-muted rounded-md text-xs overflow-auto max-h-64">
									{JSON.stringify(selectedLog.metadata, null, 2)}
								</pre>
							</div>
						)}
					</div>
				)}
			</BottomSheet>
		</div>
	);
}

function AuditLogsSkeleton() {
	return (
		<div className="space-y-4">
			<Skeleton className="h-8 w-64" />
			<Skeleton className="h-32 w-full" />
			<Skeleton className="h-96 w-full" />
		</div>
	);
}

function AuditLogsTableSkeleton() {
	return (
		<div className="space-y-2">
			{[1, 2, 3, 4, 5].map((i) => (
				<Skeleton key={i} className="h-12 w-full" />
			))}
		</div>
	);
}

export default AuditLogs;
